import {
  ExclamationCircleFilled,
  EyeFilled,
  EyeInvisibleFilled,
} from '@ant-design/icons'
import { Input } from 'antd'
import React, { CSSProperties, PropsWithChildren } from 'react'
import classes from './Input.module.scss'
import { useState } from 'react'
import { useField } from 'formik'
import Text from '../Typography/Text'

interface IErrorContainer {
  errorMessage: string
}

const ErrorContainer: React.FC<IErrorContainer> = ({
  errorMessage,
}: IErrorContainer) => {
  return (
    <div className={classes.errorFieldMessage}>
      <ExclamationCircleFilled
        style={{ color: '#FF4048', fontSize: '11px', marginLeft: '4px' }}
      />
      <Text
        style={{ marginLeft: '6px' }}
        color="redPrimary"
        fontFamily="regular"
        size={13}
      >
        {errorMessage}
      </Text>
    </div>
  )
}

export interface IInput {
  type?: 'text' | 'number' | 'email' | 'password'
  isDisabled?: boolean
  name: string
  id?: string
  placeholder?: string
  value?: string
  top?: number
  style?: CSSProperties
  labelStyle?: CSSProperties
  defaultValue?: string
  label?: string
  topSpace?: number
  required?: boolean
  bordered?: boolean
  variant?: string
  textAreaMaxLength?: number
  widthSpace?: string
  handleChange?: (value: string | number | any | undefined) => void
}

const sharedInputStyles = {
  background: '#F1F1F1',
  borderRadius: 8,
  height: '50px',
  width: '100%',
  marginTop: 10,
}

const sharedLabelStyles = {
  fontFamily: 'Inter-Regular',
  fontSize: '16px',
  fontWeight: 600,
  color: '#484C57',
}

const StyledInput: React.FC<IInput> = ({
  name,
  type = 'text',
  labelStyle,
  label = '',
  isDisabled,
  value,
  required,
  placeholder,
  topSpace,
  handleChange,
  bordered = false,
  style,
  variant = 'default',
  textAreaMaxLength = 200,
  widthSpace = '100%',
  ...props
}: IInput) => {
  const [passwordVisible, setPasswordVisible] = useState<boolean>(false)
  const [field, meta] = useField({ name })
  const isFieldError =
    required &&
    Boolean(meta.touched) &&
    Boolean(field.name) &&
    Boolean(meta.error)

  if (type === 'password') {
    return (
      <div style={{ marginTop: topSpace, width: widthSpace }}>
        <div style={{ position: 'relative' }}>
          <div
            style={{
              position: 'absolute',
              right: 20,
              bottom: -70,
              zIndex: 999,
            }}
          >
            {passwordVisible ? (
              <EyeFilled
                style={{ cursor: 'pointer', color: '#7E7E7E' }}
                onClick={() => setPasswordVisible(!passwordVisible)}
              />
            ) : (
              <EyeInvisibleFilled
                style={{ cursor: 'pointer', color: '#7E7E7E' }}
                onClick={() => setPasswordVisible(!passwordVisible)}
              />
            )}
          </div>
        </div>
        <label htmlFor={name} style={{ ...sharedLabelStyles, ...labelStyle }}>
          {label} {required && <span style={{ color: 'red' }}>*</span>}
        </label>
        <Input
          name={name}
          id={name}
          disabled={isDisabled}
          placeholder={placeholder}
          onChange={handleChange}
          type={passwordVisible ? 'text' : 'password'}
          data-testid="nusa-input"
          aria-label="nusa-input"
          value={value}
          className={
            isFieldError ? classes.input__styled__error : classes.input__styled
          }
          bordered={bordered}
          style={{
            ...sharedInputStyles,
            ...style,
          }}
          {...props}
        />
        {isFieldError && <ErrorContainer errorMessage={meta.error} />}
      </div>
    )
  }

  if (variant === 'textarea') {
    return (
      <div style={{ marginTop: topSpace, width: widthSpace }}>
        <label htmlFor={name} style={{ ...sharedLabelStyles, ...labelStyle }}>
          {label} {required && <span style={{ color: 'red' }}>*</span>}
        </label>
        <Input.TextArea
          autoSize={{ minRows: 4 }}
          maxLength={textAreaMaxLength}
          name={name}
          id={name}
          disabled={isDisabled}
          placeholder={placeholder}
          onChange={handleChange}
          data-testid="nusa-input"
          value={value}
          aria-label="nusa-input"
          bordered={bordered}
          className={
            isFieldError ? classes.input__styled__error : classes.input__styled
          }
          style={{ ...sharedInputStyles, ...style }}
        />
        {isFieldError && <ErrorContainer errorMessage={meta.error} />}
      </div>
    )
  }

  return (
    <div style={{ marginTop: topSpace, width: widthSpace }}>
      <label htmlFor={name} style={{ ...sharedLabelStyles, ...labelStyle }}>
        {label} {required && <span style={{ color: 'red' }}>*</span>}
      </label>
      <Input
        name={name}
        id={name}
        disabled={isDisabled}
        type={type}
        placeholder={placeholder}
        onChange={handleChange}
        value={value}
        data-testid="nusa-input"
        aria-label="nusa-input"
        bordered={bordered}
        className={
          isFieldError ? classes.input__styled__error : classes.input__styled
        }
        style={{ ...sharedInputStyles, ...style }}
        {...props}
      />
      {isFieldError && <ErrorContainer errorMessage={meta.error} />}
    </div>
  )
}

export default StyledInput
